import React, { useState } from 'react';
import Select from '../../../../components/UI/Select/Select';
import { Checkbox } from '../../../../components/UI/Checkbox/Checkbox';
import { Button } from '../../../../components/UI/Button/Button';
import { QuestionRequest, OptionRequest } from '../../../../api/assignments/dto/QuestionRequests.dto';
import { AssignmentQuestion, QuestionType, QUESTION_TYPES } from '../../../../models/assignments';
import { isChoiceType, normalizeQuestionForType, validateQuestion } from './validateQuestion';
import { OptionsEditor } from './OptionsEditor';
import styles from './assignment.module.css';

export interface QuestionCardProps {
  // null = новый черновик, ещё не сохранённый на бэке.
  question: AssignmentQuestion | null;
  index: number;
  onSave: (_req: QuestionRequest) => Promise<void>;
  onDelete: () => Promise<void>;
  onDiscardDraft?: () => void;
  disabled?: boolean;
}

function optionsFromQuestion(question: AssignmentQuestion | null): OptionRequest[] {
  // Id вариантов на бэке пересоздаются при каждом сохранении вопроса,
  // поэтому в локальном черновике их не храним.
  return (question?.options ?? []).map((option) => ({
    option_text: option.option_text,
    is_correct: option.is_correct,
  }));
}

// Карточка одного вопроса задания: локальное редактирование типа, текста,
// обязательности и вариантов ответа с сохранением/удалением через колбэки родителя.
export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  index,
  onSave,
  onDelete,
  onDiscardDraft,
  disabled = false,
}) => {
  const isDraft = question === null;

  const initialType: QuestionType = question?.type ?? 'single_choice';
  const initialQuestionText = question?.question_text ?? '';
  const initialIsRequired = question ? question.is_required : true;
  const initialOptions = optionsFromQuestion(question);

  const [type, setType] = useState<QuestionType>(initialType);
  const [questionText, setQuestionText] = useState<string>(initialQuestionText);
  const [isRequired, setIsRequired] = useState<boolean>(initialIsRequired);
  const [options, setOptions] = useState<OptionRequest[]>(initialOptions);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  const isDirty =
    isDraft ||
    type !== initialType ||
    questionText !== initialQuestionText ||
    isRequired !== initialIsRequired ||
    JSON.stringify(options) !== JSON.stringify(initialOptions);

  const handleTypeChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
    const nextType = e.target.value as QuestionType;
    const normalized = normalizeQuestionForType({ type: nextType, question_text: questionText, options });
    setType(nextType);
    setOptions(normalized.options);
  };

  const handleSave = async () => {
    const draft = normalizeQuestionForType({ type, question_text: questionText, options });
    const validationError = validateQuestion(draft);

    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setSaving(true);
    try {
      await onSave({
        type: draft.type,
        question_text: questionText,
        is_required: isRequired,
        options: draft.options,
      });
    } catch {
      // Родитель уже показал уведомление об ошибке и оставил карточку «не сохранено».
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isDraft) {
      onDiscardDraft?.();
      return;
    }

    if (window.confirm('Удалить вопрос?')) {
      try {
        await onDelete();
      } catch {
        // Родитель уже показал уведомление об ошибке удаления.
      }
    }
  };

  const controlsDisabled = disabled || saving;

  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <span className={styles.title}>Вопрос {index + 1}</span>
        {isDirty && <span className={styles.unsavedBadge}>Не сохранено</span>}
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>Тип вопроса</label>
        <fieldset disabled={controlsDisabled} style={{ border: 'none', margin: 0, padding: 0 }}>
          <Select
            aria-label="Тип вопроса"
            listCategories={QUESTION_TYPES}
            selectedValue={type}
            onChange={handleTypeChange}
          />
        </fieldset>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>Текст вопроса</label>
        <textarea
          className={styles.textArea}
          aria-label="Текст вопроса"
          value={questionText}
          onChange={(e) => setQuestionText(e.target.value)}
          disabled={controlsDisabled}
        />
      </div>

      <Checkbox
        label="Обязательный"
        checked={isRequired}
        onChange={(e) => setIsRequired(e.target.checked)}
        disabled={controlsDisabled}
      />

      {isChoiceType(type) && (
        <OptionsEditor
          type={type as 'single_choice' | 'multiple_choice'}
          options={options}
          onChange={setOptions}
          disabled={controlsDisabled}
        />
      )}

      {error && <div role="alert" className={styles.error}>{error}</div>}

      <div className={styles.actions}>
        <Button appearance="auth" onClick={handleSave} disabled={controlsDisabled}>
          Сохранить
        </Button>
        <Button appearance="link" onClick={handleDelete} disabled={controlsDisabled}>
          Удалить
        </Button>
      </div>
    </div>
  );
};

export default QuestionCard;
